var _a;
import { __assign } from "tslib";
import { cloneEvent, logNotification } from '@microsoft/oteljs';
import { hookUp1DSNotifications, getAppInsights } from './ApplicationInsights';
import { addOneDSFields, AppName, AppPlatform, AppVersion, getTimestamp, preprocessEvent, ReleaseAudienceGroup, tryCatch, voidValue, WacRing } from './InternalUtils';
var AllowedPartAMapping = (_a = {},
    _a[3 /* AllowedPartA.AppName */] = AppName,
    _a[4 /* AllowedPartA.AppPlatform */] = AppPlatform,
    _a[5 /* AllowedPartA.AppVersion */] = AppVersion,
    _a[2 /* AllowedPartA.WacRing */] = WacRing,
    _a[1 /* AllowedPartA.ReleaseAudienceGroup */] = ReleaseAudienceGroup,
    _a);
var ARIA_DNM_OFFICE_TELEMETRY_TOKEN = '67074e509c164bcaaf85b53c0ad0cde2-13c1c869-7901-4e72-b536-9e754e3b7413-6518'; // Office.DNM.Telemetry
var DNMHandler = /** @class */ (function () {
    function DNMHandler(properties) {
        var _this = this;
        this.eventsProcessed = 0;
        var _directNumericApplicationInsights;
        var _oneDSSink;
        var _specialEventType = 3 /* EventType.DirectNumericEvent */;
        function getTenantToken(telemetryEvent) {
            if (!telemetryEvent.telemetryProperties || !telemetryEvent.telemetryProperties.dnmToken) {
                logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () { return 'Missing DNM Token'; });
                return undefined;
            }
            return telemetryEvent.telemetryProperties.dnmToken;
        }
        function getDnmPartAFields(partAFields, dnmAllowedPartA) {
            var dnmPartA = {};
            if (dnmAllowedPartA) {
                dnmAllowedPartA.forEach(function (partA) {
                    var fieldName = AllowedPartAMapping[partA];
                    dnmPartA[fieldName] = partAFields[fieldName];
                });
            }
            return dnmPartA;
        }
        var getOneDSEvent = function (telemetryEvent) {
            var _a;
            // Clone the event, because preprocessEvent might modify it
            var internalEvent = cloneEvent(telemetryEvent);
            // Only allow the EventThrottler Preprocessor in the DNM handler
            // The sampler Preprocessor adds non-anonymous fields
            var preprocessors = [];
            _oneDSSink.getPreprocessors().forEach(function (preprocessor) {
                if (preprocessor.name === 'EventThrottler') {
                    preprocessors.push(preprocessor);
                }
            });
            if (preprocessEvent(internalEvent, preprocessors)) {
                var eventTime = getTimestamp(internalEvent);
                // To reduce risk of a privacy incident, direct numeric events
                // will always have seconds and milliseconds zeroed out
                eventTime.setMilliseconds(0);
                eventTime.setSeconds(0);
                eventTime.setMinutes(0);
                if (internalEvent.telemetryProperties.dnmInterval === 2 /* DnmInterval.Daily */) {
                    eventTime.setHours(0);
                }
                var data = {
                    'Event.Name': internalEvent.eventName,
                    'Event.Source': 'OTelJS',
                    'Event.Time': { value: eventTime.toISOString(), propertyType: 9 /* EventPropertyType.DateTime */ }
                };
                var dnmPartA = getDnmPartAFields(_oneDSSink.getOneDSPersistentDataFields(), (_a = telemetryEvent === null || telemetryEvent === void 0 ? void 0 : telemetryEvent.telemetryProperties) === null || _a === void 0 ? void 0 : _a.dnmAllowedPartA);
                for (var fieldName in dnmPartA) {
                    data[fieldName] = dnmPartA[fieldName];
                }
                // If a field is found that makes the event invalid, drop the event
                if (!addOneDSFields(data, internalEvent.dataFields, /*prependDataToken*/ true, _specialEventType)) {
                    logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () { return 'Dropping Event: ' + internalEvent.eventName; });
                    return undefined;
                }
                var baseType = 'custom';
                var token = getTenantToken(internalEvent);
                if (!token) {
                    return undefined;
                }
                var oneDSEvent = {
                    iKey: token,
                    name: internalEvent.eventName,
                    data: data,
                    time: eventTime.toISOString(),
                    baseType: baseType,
                    ext: {
                        sdk: {
                            seq: 0
                        },
                        utc: {
                            eventFlags: 0x200000 // Drop Identifiers https://www.osgwiki.com/wiki/CommonSchema/flags
                        }
                    }
                };
                return oneDSEvent;
            }
            return undefined;
        };
        this.initialize = function (oneDSSink, oneDSSinkProperties) {
            var dnmProperties = __assign(__assign({}, oneDSSinkProperties), { endpointUrl: properties.endpointUrl, extensionConfig: __assign(__assign({}, oneDSSinkProperties.extensionConfig), { PrivacyGuardPlugin: {} }), plugins: [], nonStandardEventHandlers: [] });
            _directNumericApplicationInsights = getAppInsights(dnmProperties, ARIA_DNM_OFFICE_TELEMETRY_TOKEN);
            if (!dnmProperties.disableStatsTracking) {
                hookUp1DSNotifications(oneDSSink, _directNumericApplicationInsights);
            }
            _oneDSSink = oneDSSink;
        };
        this.shutdown = function () {
            _directNumericApplicationInsights === null || _directNumericApplicationInsights === void 0 ? void 0 : _directNumericApplicationInsights.shutdown();
        };
        this.flush = function (async) {
            _directNumericApplicationInsights === null || _directNumericApplicationInsights === void 0 ? void 0 : _directNumericApplicationInsights.flush(async);
        };
        this.canHandle = function (telemetryEventType) {
            if (telemetryEventType === _specialEventType) {
                return true;
            }
            return false;
        };
        this.processEvent = function (telemetryEvent) {
            _this.eventsProcessed++;
            return tryCatch(function () {
                var event = getOneDSEvent(telemetryEvent);
                if (event && _directNumericApplicationInsights) {
                    _directNumericApplicationInsights === null || _directNumericApplicationInsights === void 0 ? void 0 : _directNumericApplicationInsights.track(event);
                }
            }, voidValue);
        };
    }
    return DNMHandler;
}());
export { DNMHandler };
//# sourceMappingURL=DNMHandler.js.map